import { CommonModule } from '@angular/common';
import { Injector, NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { CommonsModule } from '@digitsolu/commons';
import { FlowbiteModule } from '@digitsolu/flowbite';
import { NotusAngularModule } from '@digitsolu/notus-angular';
import { UiTailwindModule } from '@digitsolu/ui-tailwind';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { OKTA_CONFIG, OktaAuthModule } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { TimelineModule } from 'primeng/timeline';
import { environment } from '../environments/environment';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app.routes';
import { AppState } from './app.states';
import { HomeComponent } from './home/home.component';
import { MainComponent } from './main/main.component';
import { LoginComponent } from './user/login/login.component';
import { LogoutComponent } from './user/logout/logout.component';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';

import {
  HttpClient,
  provideHttpClient,
  withFetch,
  withInterceptors,
  withXsrfConfiguration,
} from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import {
  LOCAL_STORAGE_ENGINE,
  NgxsStoragePluginModule,
} from '@ngxs/storage-plugin';
import { NgxsModule } from '@ngxs/store';
import { NgSwitcheryModule } from 'angular-switchery-ios';
import { ToastrModule } from 'ngx-toastr';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { RECAPTCHA_BASE_URL, RECAPTCHA_NONCE, RECAPTCHA_SETTINGS, RECAPTCHA_V3_SITE_KEY, RecaptchaModule, RecaptchaV3Module } from 'ng-recaptcha';

import { CallbackComponent } from './callback/callback.component';
import { FrameFrontComponent } from './frames/front/front.component';
import { SidebarComponent } from './frames/sidebar/sidebar.component';
import { ApplicationComponent } from './home/application/application.component';
import { HistoryComponent } from './home/history/history.component';
import { ProfileComponent } from './home/profile/profile.component';
import { SecurityComponent } from './home/security/security.component';
import { Change2faPopupComponent } from './popup/change-2fa-popup/change-2fa-popup.component';
import { ChangePasswordPopupComponent } from './popup/change-password-popup/change-password-popup.component';
import { Choose2faPopupComponent } from './popup/choose-2fa-popup/choose-2fa-popup.component';
import { EmailVerifyPopupComponent } from './popup/email-verify-popup/email-verify-popup.component';
import { SmsVerifyPopupComponent } from './popup/sms-verify-popup/sms-verify-popup.component';
import { ForgotPasswordSubmitComponent } from './user/forgot-password-submit/forgot-password-submit.component';
import { ForgotPasswordValidateComponent } from './user/forgot-password-validate/forgot-password-validate.component';
import { ForgotPasswordComponent } from './user/forgot-password/forgot-password.component';
import { LoginCallbackComponent } from './user/login-callback/login-callback.component';
import { LoginValidateComponent } from './user/login-validate/login-validate.component';
import { PortalLoginComponent } from './user/portallogin/portallogin.component';
import { RegisterValidateComponent } from './user/register-validate/register-validate.component';
import { RegisterComponent } from './user/register/register.component';
import { VerificationComponent } from './verification/verification.component';
import { CodeInputModule } from './widgets/code-input/code-input.module';
import { WidgetEmailCodeComponent } from './widgets/email-code/email-code.component';
import { FormForgotPasswordComponent } from './widgets/form-forgot-password/form-forgot-password.component';
import { FormLoginComponent } from './widgets/form-login/form-login.component';
import { FormRegisterComponent } from './widgets/form-register/form-register.component';
import { FormResetPasswordComponent } from './widgets/form-reset-password/form-reset-password.component';
import { WidgetSmsCodeComponent } from './widgets/sms-code/sms-code.component';
import { CountryDropdownComponent } from './widgets/country-dropdown/country-dropdown.component';

import { config as faconfig } from '@fortawesome/fontawesome-svg-core';
import * as _ from 'lodash';
import { authInterceptor } from './interceptors/auth/auth.interceptor';
import { recoveryInterceptor } from './interceptors/recovery/recovery.interceptor';
import { EmailChangePopupComponent } from './popup/email-change-popup/email-change-popup.component';
import { PhoneChangePopupComponent } from './popup/phone-change-popup/phone-change-popup.component';

import { FormForgotUsernameComponent } from './user/components/form-forgot-username/form-forgot-username.component';
import { ForgotUsernameSubmitComponent } from './user/components/forgot-username-submit/forgot-username-submit.component';
import { UsernameState } from './user/store';
import { ForgotUsernameComponent } from './user/components/forgot-username/forgot-username.component';
faconfig.autoAddCss = false;

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

// const config: OktaAuthOptions = {
//   issuer: 'https://' + environment.oktaDomain,
//   clientId: environment.oktaClientId,
//   redirectUri: window.location.origin + '/login/callback',
//   responseType: 'code',
//   storageManager: {
//     token: {
//       storageType: 'localStorage'
//     }
//   },
//   cookies: {
//     secure: true,
//     sameSite: 'lax',
//   },
// }
const config = {
  clientId: environment.oktaClientId,
  // issuer: "https://cic-onepass-uat.okta.com/oauth2/default",
  issuer: `https://${environment.oktaDomain}/oauth2/default`,
  redirectUri: `${window.location.origin}/login/callback`,
  scopes: ['openid', 'profile', 'email'],
  pkce: true,
  testing: {
    disableHttpsCheck: true,
  },
};

@NgModule({
  declarations: [
    LoginComponent,
    LoginCallbackComponent,
    PortalLoginComponent,
    CallbackComponent,
    RegisterComponent,
    RegisterValidateComponent,
    LoginValidateComponent,
    ForgotPasswordComponent,
    ForgotPasswordValidateComponent,
    ForgotPasswordSubmitComponent,
    ForgotUsernameComponent,
    ForgotUsernameSubmitComponent,
    PhoneChangePopupComponent,
    EmailChangePopupComponent,

    HomeComponent,
    ProfileComponent,
    ApplicationComponent,
    SecurityComponent,
    HistoryComponent,
    LogoutComponent,

    FrameFrontComponent,
    SidebarComponent,

    FormLoginComponent,
    FormRegisterComponent,
    FormForgotPasswordComponent,
    FormForgotUsernameComponent,
    FormResetPasswordComponent,
    Choose2faPopupComponent,
    ChangePasswordPopupComponent,
    Change2faPopupComponent,
    SmsVerifyPopupComponent,
    EmailVerifyPopupComponent,
    WidgetSmsCodeComponent,
    WidgetEmailCodeComponent,
    CountryDropdownComponent,

    VerificationComponent,

    AppComponent,
    MainComponent,
    LogoutComponent,
  ],
  imports: [
    NgxsModule.forRoot([AppState], {
      developmentMode: !environment.production,
      compatibility: {
        strictContentSecurityPolicy: true,
      },
    }),
    NgxsModule.forFeature([UsernameState]),
    NgxsStoragePluginModule.forRoot({
      key: [
        {
          key: AppState,
          engine: LOCAL_STORAGE_ENGINE,
        },
      ],
      beforeSerialize: (obj, key) => {
        const newObj = _.pickBy(obj, (v, k) => {
          return !['emailEnrollResponse', 'smsEnrollResponse'].includes(k);
        });

        return newObj;
      },
      afterDeserialize: (obj, key) => obj,
    }),
    NgxsReduxDevtoolsPluginModule.forRoot({
      disabled: environment.production,
    }),
    CommonModule,
    ReactiveFormsModule,
    CommonsModule,
    AppRoutingModule,
    TableModule,
    UiTailwindModule,
    NotusAngularModule,
    FlowbiteModule,
    TranslateModule.forRoot({
      defaultLanguage: 'zh-Hant',
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    BrowserAnimationsModule,
    NgSwitcheryModule,
    ToastrModule.forRoot(),
    FontAwesomeModule,
    OktaAuthModule,
    TimelineModule,
    RecaptchaModule,
    RecaptchaV3Module,
    // AuthModule.forRoot({
    //   config: [
    //     {
    //       configId: 'normal',
    //       authority: 'https://' + environment.oktaDomain,
    //       postLogoutRedirectUri: environment.portalDomain,
    //       redirectUrl: environment.portalDomain + 'portallogin/callback',
    //       clientId: environment.oktaAuthAppId,
    //       scope: 'openid profile email phone address offline_access',
    //       responseType: 'code',
    //       silentRenew: true,
    //       useRefreshToken: true,
    //       historyCleanupOff: false,
    //       autoUserInfo: true,
    //       logLevel: LogLevel.Debug,
    //     },
    //     {
    //       configId: 'low',
    //       authority: 'https://' + environment.oktaDomain,
    //       postLogoutRedirectUri: environment.portalDomain,
    //       redirectUrl: environment.portalDomain + 'portallogin/callback',
    //       clientId: environment.oktaAuthAppIdLowRisk,
    //       scope: 'openid profile email phone address offline_access',
    //       responseType: 'code',
    //       silentRenew: true,
    //       useRefreshToken: true,
    //       historyCleanupOff: false,
    //       autoUserInfo: true,
    //       logLevel: LogLevel.Debug,
    //     },
    //     {
    //       configId: 'high',
    //       authority: 'https://' + environment.oktaDomain,
    //       postLogoutRedirectUri: environment.portalDomain,
    //       redirectUrl: environment.portalDomain + 'portallogin/callback',
    //       clientId: environment.oktaAuthAppIdHighRisk,
    //       scope: 'openid profile email phone address offline_access',
    //       responseType: 'code',
    //       silentRenew: true,
    //       useRefreshToken: true,
    //       historyCleanupOff: false,
    //       autoUserInfo: true,
    //       logLevel: LogLevel.Debug,
    //     },
    //   ],
    // }),
    CodeInputModule,
    environment.plugins,
    NgxsReduxDevtoolsPluginModule.forRoot({
      disabled: environment.production,
    }),
    InputTextModule,
    NgxMaskDirective,
    NgxMaskPipe,
  ],
  providers: [
    {
      provide: 'env',
      useValue: environment,
    },
    {
      provide: OKTA_CONFIG,
      useFactory: () => {
        const oktaAuth = new OktaAuth(config);

        return {
          oktaAuth,
          onAuthRequired: (oktaAuth: OktaAuth, injector: Injector) => {
            const triggerLogin = async () => {
              await oktaAuth.signInWithRedirect();
            };

            if (
              !oktaAuth.authStateManager.getPreviousAuthState()?.isAuthenticated
            ) {
              // App initialization stage
              triggerLogin();
            } else {
              // Ask the user to trigger the login process during token autoRenew process
            }
          },
        };
      },
      // useValue: { oktaAuth }
    },
    provideHttpClient(
      withFetch(),
      withInterceptors([authInterceptor, recoveryInterceptor]),
      withXsrfConfiguration({ cookieName: 'XSRF-TOKEN', headerName: 'X-XSRF-TOKEN' })
    ),
    provideNgxMask(),
    {
      provide: RECAPTCHA_BASE_URL,
      useValue: "https://recaptcha.net/recaptcha/api.js", // use recaptcha.net script source for some of our users
    },
    {
      provide: RECAPTCHA_NONCE,
      useValue: "random_nonce_value",
    },
    {
      provide: RECAPTCHA_SETTINGS,
      useValue: {
        siteKey: environment.recaptchaV2SiteKey,
      }
    },
    {
      provide: RECAPTCHA_V3_SITE_KEY,
      useValue: environment.recaptchaV3SiteKey,
    }
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
